import { useMemo } from 'react';
import { Play, Sparkles, Lock, Infinity as InfinityIcon } from 'lucide-react';
import { LEVELS, BIOMES } from '@/lib/calmCommandLevels';
import StarBadge from './StarBadge';

/**
 * CalmCommandLevelSelect — 6 biomes × 5 levels, plus an Endless Calm tile.
 */
export default function CalmCommandLevelSelect({ cc, onPick, onEndless }) {
  const grouped = useMemo(() => BIOMES.map(b => ({
    ...b,
    levels: LEVELS.filter(l => l.biome === b.id),
  })), []);

  // Gated progression — clear a level to unlock the next.
  const highestUnlocked = cc?.highestUnlocked ?? 1;

  return (
    <div className="space-y-6" data-testid="cc-level-select">
      {grouped.map((b) => (
        <div key={b.id} className="rounded-2xl p-4 lg:p-5 border border-white/5"
             style={{ background: `linear-gradient(135deg, ${b.bg[0]} 0%, ${b.bg[1]} 100%)` }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: b.accent }}>
                Biome {b.id}
              </p>
              <h4 className="font-heading text-xl text-white mt-0.5">{b.name}</h4>
              <p className="text-white/55 text-xs mt-0.5">{b.tagline}</p>
            </div>
            <div className="text-right">
              <p className="text-[9px] uppercase tracking-widest text-white/40">Stars</p>
              <p className="font-heading text-lg text-white tabular-nums">
                {b.levels.reduce((sum, l) => sum + (cc?.levelStars?.[l.id] ?? 0), 0)}
                <span className="text-white/40">/{b.levels.length * 3}</span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-2">
            {b.levels.map((l) => {
              const stars = cc?.levelStars?.[l.id] ?? 0;
              const locked = l.id > highestUnlocked;
              return (
                <button
                  key={l.id}
                  type="button"
                  disabled={locked}
                  onClick={() => onPick(l.id)}
                  data-testid={`cc-level-${l.id}`}
                  className={`relative rounded-xl px-2 py-2.5 text-center transition-all border ${
                    locked
                      ? 'bg-white/5 border-white/5 cursor-not-allowed'
                      : stars > 0
                        ? 'bg-white/10 border-white/15 hover:bg-white/15 hover:-translate-y-0.5'
                        : 'bg-white/[0.06] border-white/10 hover:bg-white/10 hover:-translate-y-0.5'
                  }`}
                  style={l.isBoss && !locked ? {
                    boxShadow: `0 0 0 1px ${b.glow}55, inset 0 0 12px ${b.glow}22`,
                  } : undefined}
                >
                  <p className={`font-heading text-lg tabular-nums leading-none ${locked ? 'text-white/25' : 'text-white'}`}>{l.id}</p>
                  <div className="mt-1 flex items-center justify-center min-h-[14px]">
                    {locked
                      ? <Lock size={11} className="text-white/25" strokeWidth={2}/>
                      : <StarBadge count={stars} size={9} testid={`cc-level-${l.id}-stars`}/>}
                  </div>
                  {l.isBoss && !locked && (
                    <span className="absolute -top-1.5 -right-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full"
                          style={{ background: b.glow, color: '#0B0F1E' }}>
                      BOSS
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <button type="button" onClick={onEndless} data-testid="cc-endless-mode"
        className="w-full rounded-2xl p-4 text-left transition-all border bg-gradient-to-br from-[#0F1A2E] to-[#3a0f7a] border-cyan-300/30 hover:border-cyan-300/60 hover:-translate-y-0.5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-cyan-300 flex items-center gap-1.5">
              <InfinityIcon size={12} strokeWidth={2}/> Endless Calm
            </p>
            <p className="font-heading text-lg mt-1 text-white">Guard until the last anchor falls</p>
            <p className="text-white/60 text-xs mt-1.5 leading-relaxed">
              No timer. Waves scale as you go. Every neutralized stress wave keeps you in the game.
            </p>
          </div>
          <Play size={16} className="text-cyan-300 mt-1 shrink-0" strokeWidth={2}/>
        </div>
        {(cc?.endlessBest ?? 0) > 0 && (
          <p className="text-white/60 text-xs mt-2">Best: <span className="text-cyan-300 tabular-nums">{cc.endlessBest}</span> neutralized</p>
        )}
        <p className="text-white/40 text-[10px] mt-2 uppercase tracking-wider">
          <Sparkles size={10} className="inline text-cyan-300 mr-1"/> Bonus focus points on high runs
        </p>
      </button>
    </div>
  );
}
